import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Download, RefreshCw, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const BookCoverDesignModal = ({ open, onOpenChange, project, projectId, API }) => {
  const [coverStyle, setCoverStyle] = useState("professional");
  const [coverMood, setCoverMood] = useState("dramatic");
  const [customPrompt, setCustomPrompt] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (open && !loaded) {
      (async () => {
        try {
          const res = await axios.get(`${API}/cover/${projectId}`);
          if (res.data.has_cover) {
            setCoverImage(res.data.image_base64);
            setCoverStyle(res.data.style || "professional");
            setCoverMood(res.data.mood || "dramatic");
          }
        } catch {
          // No existing cover
        }
        setLoaded(true);
      })();
    }
  }, [open, loaded, API, projectId]);

  const generateCover = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/generate-cover/${projectId}`, {
        style: coverStyle,
        mood: coverMood,
        include_title: true,
        custom_prompt: customPrompt || null
      }, { timeout: 150000 });
      
      if (res.data.success) {
        setCoverImage(res.data.image_base64);
        toast.success("Cover generated! Your book cover is ready.");
      } else {
        toast.error(res.data.detail || "Cover generation failed");
      }
    } catch (err) {
      if (err.code === "ECONNABORTED") {
        toast.error("Cover generation timed out. Please try again.");
      } else {
        toast.error(err.response?.data?.detail || "Failed to generate cover");
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-literary flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-fuchsia-600" />
            AI Book Cover Design
          </DialogTitle>
          <DialogDescription>
            Generate a professional book cover using AI. Customize the style and mood to match your story.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Cover Style</Label>
              <Select value={coverStyle} onValueChange={setCoverStyle}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Select style" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="artistic">Artistic</SelectItem>
                  <SelectItem value="minimalist">Minimalist</SelectItem>
                  <SelectItem value="vintage">Vintage</SelectItem>
                  <SelectItem value="modern">Modern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Mood</Label>
              <Select value={coverMood} onValueChange={setCoverMood}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Select mood" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dramatic">Dramatic</SelectItem>
                  <SelectItem value="serene">Serene</SelectItem>
                  <SelectItem value="mysterious">Mysterious</SelectItem>
                  <SelectItem value="action">Action</SelectItem>
                  <SelectItem value="romantic">Romantic</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label className="text-sm font-medium mb-2 block">Custom Description (Optional)</Label>
            <Textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Describe specific elements you want on your cover..."
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave empty to auto-generate from your story details, or describe exactly what you want.
            </p>
          </div>
          
          <Button
            className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
            onClick={generateCover}
            disabled={generating}
            data-testid="generate-cover-btn"
          >
            {generating ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating Cover... (this may take up to 1 minute)</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> {coverImage ? "Regenerate Cover" : "Generate Book Cover"}</>
            )}
          </Button>
          
          {coverImage && (
            <div className="mt-4">
              <Label className="text-sm font-medium mb-2 block">Your Book Cover</Label>
              <div className="flex justify-center">
                <div className="relative group">
                  <img
                    src={`data:image/png;base64,${coverImage}`}
                    alt="Generated book cover"
                    className="max-h-[400px] rounded-lg shadow-2xl border-4 border-slate-700"
                    data-testid="cover-preview-image"
                  />
                  <div className="absolute top-4 left-0 right-0 text-center px-4">
                    <h2 className="text-2xl font-bold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] font-serif">
                      {project?.title || "Your Title"}
                    </h2>
                    {project?.series_info?.is_series && (
                      <p className="text-sm text-white/90 drop-shadow-lg mt-1">
                        {project.series_info.series_name} - Book {project.series_info.book_number}
                      </p>
                    )}
                  </div>
                  <div className="absolute bottom-4 left-0 right-0 text-center">
                    <p className="text-lg text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] font-serif">
                      {project?.author_name || "Author Name"}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center mt-4 gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = `data:image/png;base64,${coverImage}`;
                    link.download = `${(project?.title || "book-cover").replace(/\s+/g, "-").toLowerCase()}-cover.png`;
                    link.click();
                  }}
                  data-testid="download-cover-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Download Cover
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(`data:image/png;base64,${coverImage}`);
                    toast.success("Cover image copied to clipboard!");
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy Image
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground text-center mt-2">
                Tip: Download the cover and add your title/author name using a design tool like Canva for the final version.
              </p>
            </div>
          )}
          
          <div className="bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 rounded-lg p-3 text-sm">
            <p className="text-violet-800 font-medium mb-1">Cover Design Tips:</p>
            <ul className="text-violet-700 text-xs space-y-1">
              <li>- <strong>Genre matters:</strong> Romance covers differ greatly from thrillers</li>
              <li>- <strong>Less is more:</strong> Simple, iconic imagery often works best</li>
              <li>- <strong>Custom prompts:</strong> Describe key scenes or symbols from your book</li>
              <li>- <strong>Iterate:</strong> Generate multiple versions with different styles</li>
            </ul>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
